import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import { connect } from 'react-redux';
import { formValueSelector, change, submit as submitForm, reduxForm } from 'redux-form';

import OpenStreetMap from '../../OpenStreetMap/OpenStreetMap';
import RedoSearch from '../RedoSearch';
import ExpandToggle from '../ExpandToggle/ExpandToggle';
import submit from '../SearchForm/submit';
import { setPersonalizedValues } from '../../../actions/personalized';
import { formatURL } from '../../../helpers/formatURL';
import s from './MapResults.css';

const COLOMBIA_CENTER = { lat: 4.5709, lng: -74.2973 };

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function currencyLabel(currency, amount) {
  const numericAmount = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: currency || 'COP', maximumFractionDigits: 0 }).format(numericAmount);
  } catch (error) {
    return `${currency || 'COP'} ${numericAmount.toLocaleString('es-CO')}`;
  }
}

function getCenter(props) {
  const { personalized, initialFilter, searchSettings, results } = props;
  if (personalized && personalized.lat && personalized.lng) return { lat: Number(personalized.lat), lng: Number(personalized.lng) };
  if (initialFilter && initialFilter.lat && initialFilter.lng) return { lat: Number(initialFilter.lat), lng: Number(initialFilter.lng) };
  if (results && results.length) return { lat: Number(results[0].lat), lng: Number(results[0].lng) };
  if (searchSettings && searchSettings.defaultLat && searchSettings.defaultLng) return { lat: Number(searchSettings.defaultLat), lng: Number(searchSettings.defaultLng) };
  return COLOMBIA_CENTER;
}

function buildMarkers(results, markerHighlight) {
  return (results || []).map(item => {
    const price = currencyLabel(item.listingData.currency, item.listingData.basePrice);
    const title = escapeHtml(item.title);
    const placeType = item.settingsData && item.settingsData[0] && item.settingsData[0].listsettings
      ? escapeHtml(item.settingsData[0].listsettings.itemName)
      : 'Alojamiento rural';
    const route = `/rooms/${formatURL(item.title)}-${item.id}`;
    return {
      id: item.id,
      lat: Number(item.lat),
      lng: Number(item.lng),
      title: item.title,
      label: price,
      highlighted: markerHighlight && markerHighlight.id === item.id,
      popupHtml: `<article class="osm-listing-card"><div class="osm-listing-card__eyebrow">${placeType}</div><h3>${title}</h3><p>${escapeHtml(price)} por noche · hasta ${Number(item.personCapacity) || 1} huéspedes</p><a href="${route}" target="_blank" rel="noopener noreferrer">Ver alojamiento <span>→</span></a></article>`,
    };
  }).filter(marker => Number.isFinite(marker.lat) && Number.isFinite(marker.lng));
}

class MapResults extends React.Component {
  static propTypes = {
    initialFilter: PropTypes.object,
    searchSettings: PropTypes.object,
    results: PropTypes.array,
    personalized: PropTypes.object,
    change: PropTypes.func,
    submitForm: PropTypes.func,
    setPersonalizedValues: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      center: getCenter(props),
      markers: buildMarkers(props.results, props.markerHighlight),
      userInteracted: false,
    };
    this.handleInteractionStart = this.handleInteractionStart.bind(this);
    this.handleMoveEnd = this.handleMoveEnd.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.results !== this.props.results || prevProps.markerHighlight !== this.props.markerHighlight) {
      this.setState({ markers: buildMarkers(this.props.results, this.props.markerHighlight) });
    }
    if (prevProps.initialFilter !== this.props.initialFilter && !this.state.userInteracted) {
      this.setState({ center: getCenter(this.props) });
    }
  }

  componentWillUnmount() {
    this.props.change('initialLoad', true);
  }

  async handleInteractionStart() {
    if (!this.state.userInteracted) this.setState({ userInteracted: true });
    await this.props.change('initialLoad', false);
  }

  async handleMoveEnd(viewport) {
    if (!this.state.userInteracted || !this.props.searchByMapValue) return;
    const { change, submitForm, setPersonalizedValues } = this.props;
    await change('lat', viewport.lat);
    await change('lng', viewport.lng);
    await change('sw_lat', viewport.sw_lat);
    await change('sw_lng', viewport.sw_lng);
    await change('ne_lat', viewport.ne_lat);
    await change('ne_lng', viewport.ne_lng);
    await change('zoomLevel', viewport.zoom);
    await change('currentPage', 1);
    await setPersonalizedValues({ name: 'lat', value: Number(viewport.lat) });
    await setPersonalizedValues({ name: 'lng', value: Number(viewport.lng) });
    await setPersonalizedValues({ name: 'southWest', value: { lat: viewport.sw_lat, lng: viewport.sw_lng } });
    await setPersonalizedValues({ name: 'northEast', value: { lat: viewport.ne_lat, lng: viewport.ne_lng } });
    await submitForm('SearchForm');
  }

  render() {
    const { center, markers } = this.state;
    const fitMarkers = Boolean(this.props.initialLoad && !this.props.searchByMapValue);
    return <div className={cx(s.mapCanvas, 'searchMap')}>
      <OpenStreetMap
        center={center}
        zoom={12}
        minZoom={3}
        maxZoom={18}
        height="100%"
        markers={markers}
        fitMarkers={fitMarkers}
        onInteractionStart={this.handleInteractionStart}
        onMoveEnd={this.handleMoveEnd}
      />
      <div className={s.responsiveView}><RedoSearch /></div>
      <div className={s.responsiveView}><ExpandToggle /></div>
    </div>;
  }
}

MapResults = reduxForm({ form: 'SearchForm', onSubmit: submit, destroyOnUnmount: false })(MapResults);

const selector = formValueSelector('SearchForm');
const mapState = state => ({
  results: state.search.data,
  total: state.search.count,
  personalized: state.personalized,
  searchByMapValue: selector(state, 'searchByMap'),
  initialLoad: selector(state, 'initialLoad'),
  markerHighlight: selector(state, 'markerHighlight'),
});

export default injectIntl(withStyles(s)(connect(mapState, { change, submitForm, setPersonalizedValues })(MapResults)));
